import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload, Loader2 } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onProjectCreated: (project: any) => void;
}

const CreateProjectModal = ({ isOpen, onClose, userId, onProjectCreated }: CreateProjectModalProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { file, handleFileChange } = useFileUpload();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('Creating project for user:', userId);
      let media_url = null;
      let media_type = null;

      if (file) {
        const fileExt = file.name.split('.').pop();
        const filePath = `${userId}/${Math.random()}.${fileExt}`;

        console.log('Uploading file:', filePath);
        const { error: uploadError } = await supabase.storage
          .from('portfolio')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('portfolio')
          .getPublicUrl(filePath);

        media_url = publicUrl;
        media_type = file.type.startsWith('image/') ? 'image' : 'video';
      }
      
      // First try to get existing portfolio
      const { data: portfolios, error: portfolioError } = await supabase
        .from('portfolios')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (portfolioError) {
        console.error('Error fetching portfolio:', portfolioError);
        throw portfolioError;
      }

      let portfolioId = portfolios?.id;

      // If no portfolio exists, create one
      if (!portfolioId) {
        console.log('No portfolio found, creating new one');
        const { data: newPortfolio, error: createPortfolioError } = await supabase
          .from('portfolios')
          .insert({
            user_id: userId,
            title: 'My Portfolio'
          })
          .select()
          .single();

        if (createPortfolioError) {
          console.error('Error creating portfolio:', createPortfolioError);
          throw createPortfolioError;
        }
        
        console.log('Created new portfolio:', newPortfolio);
        portfolioId = newPortfolio.id;
      }

      // Create the portfolio item
      console.log('Creating portfolio item with portfolio_id:', portfolioId);
      const { data, error } = await supabase
        .from('portfolio_items')
        .insert({
          portfolio_id: portfolioId,
          title,
          description,
          media_url,
          media_type
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating portfolio item:', error);
        throw error;
      }

      console.log('Created portfolio item:', data);
      onProjectCreated(data);
      
      toast({
        title: "Success",
        description: "Project created successfully"
      });
      onClose();
    } catch (error: any) {
      console.error('Error creating project:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message === "Payload too large" 
          ? "File size must be less than 50MB" 
          : "Failed to create project"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogTitle>Create New Project</DialogTitle>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium mb-1">
              Title
            </label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-1">
              Description
            </label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div>
            <label htmlFor="media" className="block text-sm font-medium mb-1">
              Media (Optional)
            </label>
            <Input
              id="media"
              type="file"
              accept="image/*,video/*"
              onChange={handleFileChange}
              className="mb-2"
            />
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Maximum file size: 50MB
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Videos should be 15 seconds or less and are intended as quick previews/trailers of your work.
                For longer content, consider linking to external platforms in the description.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Create Project
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectModal;